import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import type { TourStep } from '../../../types/onboarding';
import { ONBOARDING_EVENTS } from '../onboardingEventNames';
import { TOUR_DOM_EVENTS } from '../onboardingDomEvents';
import {
  OnboardingProgressTracker,
  type OnboardingProgressTrackerRef,
} from '../OnboardingProgressTracker';
import { SkipConfirmationDialog } from '../SkipConfirmationDialog';
import { MuiTourEngine } from './MuiTourEngine';
import { logger } from '../../../utils/logger';
import { useLearningLabMissionOptional } from '../../learning-lab/mission/LearningLabMissionProvider';
import { getLearningLabPlaybook } from '../../../../shared/onboarding/learningLabPlaybooks';
import { getPlaybookIncompleteStepTitlesFromTourIndex } from '../../../../shared/onboarding/buildMissionTourSteps';
import type { AdvanceGate } from '../../../../shared/onboarding/missionTypes';
import { shouldShowMissionManualNext } from '../../learning-lab/mission/missionOrchestrator';
import { TOUR_MISSION_DIALOG_Z_INDEX_OFFSET } from '../../../../shared/onboarding/missionViewport';
import { coerceTourPlainText } from '../../../../shared/utils/coerceEventListingText';

type TourEngineHostProps = {
  run: boolean;
  steps: TourStep[];
  onTourEnd?: () => void;
  onStepChange?: (step: TourStep, index: number) => void;
};

export const TourEngineHost: React.FC<TourEngineHostProps> = ({
  run,
  steps,
  onTourEnd,
  onStepChange,
}) => {
  const {
    state,
    config,
    skipTour,
    stopTour,
    completeStep,
    setCurrentStepIndex,
    getAllTours,
  } = useOnboarding();
  const mission = useLearningLabMissionOptional();
  const location = useLocation();
  const trackerRef = useRef<OnboardingProgressTrackerRef>(null);
  const refreshTargetRef = useRef<(() => void) | null>(null);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [missingTargetOpen, setMissingTargetOpen] = useState(false);
  const [missingTargetMessage, setMissingTargetMessage] = useState('');
  const [celebrationOpen, setCelebrationOpen] = useState(false);

  const index = state.currentStepIndex;

  const currentTourDisplayName = useMemo(() => {
    if (!state.currentFlowId) {
      return 'this tour';
    }
    const tour = getAllTours().find((t) => t.id === state.currentFlowId);
    const name = coerceTourPlainText(tour?.name, '').trim();
    return name.length > 0 ? name : 'this tour';
  }, [getAllTours, state.currentFlowId]);

  const skipStepTitles = useMemo(() => {
    if (!state.missionMode || !state.currentFlowId) {
      return undefined;
    }
    const playbook = getLearningLabPlaybook(state.currentFlowId);
    if (!playbook) {
      return undefined;
    }
    return getPlaybookIncompleteStepTitlesFromTourIndex(steps, index, playbook);
  }, [index, state.currentFlowId, state.missionMode, steps]);

  const handleSkipConfirmed = useCallback(() => {
    setShowSkipDialog(false);
    trackerRef.current?.track(ONBOARDING_EVENTS.TOUR_SKIPPED, {
      flowId: state.currentFlowId,
      stepIndex: index,
    });
    skipTour();
    onTourEnd?.();
  }, [index, onTourEnd, skipTour, state.currentFlowId]);

  const handleRequestSkip = useCallback(() => {
    if (!config.allowSkip) return;
    if (config.showSkipConfirmation) {
      setShowSkipDialog(true);
      return;
    }
    handleSkipConfirmed();
  }, [config.allowSkip, config.showSkipConfirmation, handleSkipConfirmed]);

  useEffect(() => {
    if (!run || !config.disableScroll) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [config.disableScroll, run]);

  useEffect(() => {
    const onRequestSkip = () => {
      handleRequestSkip();
    };
    window.addEventListener(TOUR_DOM_EVENTS.REQUEST_TOUR_SKIP, onRequestSkip);
    return () =>
      window.removeEventListener(TOUR_DOM_EVENTS.REQUEST_TOUR_SKIP, onRequestSkip);
  }, [handleRequestSkip]);

  const callbacks = useMemo(
    () => ({
      onStepView: (stepIndex: number, step: TourStep) => {
        trackerRef.current?.track(ONBOARDING_EVENTS.STEP_VIEWED, {
          stepIndex,
          stepName: step.stepName,
          flowId: state.currentFlowId,
        });
        onStepChange?.(step, stepIndex);
      },
      onStepComplete: (stepIndex: number, step: TourStep) => {
        trackerRef.current?.track(ONBOARDING_EVENTS.STEP_COMPLETED, {
          stepIndex,
          stepName: step.stepName,
          flowId: state.currentFlowId,
        });
        if (step.stepName) {
          void completeStep(step.stepName, {
            stepIndex,
            totalSteps: steps.length,
          }).catch((error) => {
            logger.warn('Failed to complete step (non-blocking)', {
              error,
              stepName: step.stepName,
            });
          });
        }
      },
      onComplete: () => {
        trackerRef.current?.track(ONBOARDING_EVENTS.TOUR_COMPLETED, {
          flowId: state.currentFlowId,
          stepIndex: index,
        });
        setCelebrationOpen(true);
        stopTour(state.currentFlowId);
        onTourEnd?.();
      },
      onSkip: () => {
        handleSkipConfirmed();
      },
      onTargetMissing: (stepIndex: number, step: TourStep) => {
        const gate = step.metadata?.advanceGate as AdvanceGate | undefined;
        const allowsManualAdvance = Boolean(
          state.missionMode &&
            gate &&
            shouldShowMissionManualNext(gate, {
              targetReady: false,
              targetOccluded: mission?.targetOccluded ?? false,
            }),
        );
        if (allowsManualAdvance || (state.missionMode && mission?.showFallbackAction)) {
          trackerRef.current?.track(ONBOARDING_EVENTS.TARGET_NOT_FOUND, {
            stepIndex,
            stepName: step.stepName,
            target: step.target,
          });
          return;
        }

        const titleText = coerceTourPlainText(step.title, '');
        const label = titleText ? `"${titleText}"` : 'this step';
        const optional = step.metadata?.optional === true;
        const occluded = mission?.targetOccluded === true;
        if (state.missionMode && occluded) {
          setMissingTargetMessage(
            `${label} is hidden behind the mission panel or off-screen. Collapse the panel, tap Show me, or use the in-panel fallback.`,
          );
        } else if (state.missionMode && optional) {
          setMissingTargetMessage(
            `We couldn't find ${label}. You can retry or skip this optional step.`,
          );
        } else {
          setMissingTargetMessage(
            `We couldn't find ${label}. It may be hidden or unavailable.`,
          );
        }
        setMissingTargetOpen(true);
        trackerRef.current?.track(ONBOARDING_EVENTS.TARGET_NOT_FOUND, {
          stepIndex,
          stepName: step.stepName,
          target: step.target,
        });
        if (stepIndex === 0 && !state.missionMode) {
          stopTour();
          onTourEnd?.();
        } else if (!state.missionMode) {
          setCurrentStepIndex(stepIndex + 1);
        }
      },
    }),
    [
      completeStep,
      handleSkipConfirmed,
      index,
      onStepChange,
      onTourEnd,
      state.currentFlowId,
      state.missionMode,
      mission?.targetOccluded,
      mission?.showFallbackAction,
      steps.length,
      stopTour,
      setCurrentStepIndex,
    ],
  );

  useEffect(() => {
    if (!state.missionMode || !state.isRunning) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      refreshTargetRef.current?.();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search, state.isRunning, state.missionMode]);

  useEffect(() => {
    if (!mission || !state.missionMode) {
      mission?.registerAdvanceHandlers(null);
      return undefined;
    }

    mission.registerAdvanceHandlers({
      onStepComplete: (stepIndex, step) => {
        callbacks.onStepComplete(stepIndex, step);
      },
      onComplete: () => {
        callbacks.onComplete();
      },
      setStepIndex: setCurrentStepIndex,
      refreshTarget: () => {
        refreshTargetRef.current?.();
        setCurrentStepIndex(state.currentStepIndex);
      },
    });

    return () => {
      mission.registerAdvanceHandlers(null);
    };
  }, [
    callbacks,
    mission,
    setCurrentStepIndex,
    state.currentStepIndex,
    state.missionMode,
  ]);

  const currentStep = steps[index];
  const optionalStep = currentStep?.metadata?.optional === true;
  const advanceGate = currentStep?.metadata?.advanceGate as AdvanceGate | undefined;
  const showMissionSpotlightNext = Boolean(
    state.missionMode &&
      advanceGate &&
      mission &&
      shouldShowMissionManualNext(advanceGate, {
        targetReady: mission.targetReady,
        targetOccluded: mission.targetOccluded,
      }),
  );
  const missionDialogZIndex = config.zIndex + TOUR_MISSION_DIALOG_Z_INDEX_OFFSET;

  return (
    <>
      <OnboardingProgressTracker
        ref={trackerRef}
        tourName={state.currentFlowId ?? 'default'}
      />

      <MuiTourEngine
        run={run}
        steps={steps}
        index={index}
        spotlightPadding={config.spotlightPadding}
        zIndex={config.zIndex}
        showSkipButton={config.showSkipButton}
        allowSkip={config.allowSkip}
        callbacks={callbacks}
        onRequestSkip={handleRequestSkip}
        onIndexChange={setCurrentStepIndex}
        missionMode={state.missionMode}
        onRegisterRefresh={(refresh) => {
          refreshTargetRef.current = refresh;
        }}
        onTargetVisibilityChange={(visibility) => {
          mission?.setTargetVisibility(visibility);
        }}
        showMissionSpotlightNext={showMissionSpotlightNext}
        onMissionSpotlightNext={() => mission?.advanceMissionStep()}
      />

      <SkipConfirmationDialog
        open={showSkipDialog}
        onConfirm={handleSkipConfirmed}
        onCancel={() => setShowSkipDialog(false)}
        tourName={currentTourDisplayName}
        remainingSteps={Math.max(0, steps.length - index - 1)}
        remainingStepTitles={skipStepTitles}
        zIndex={config.zIndex + 1300}
      />

      <Dialog
        open={missingTargetOpen}
        onClose={() => setMissingTargetOpen(false)}
        BackdropProps={{
          sx: {
            zIndex: missionDialogZIndex,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          },
        }}
        sx={{
          zIndex: missionDialogZIndex,
          '& .MuiDialog-container': { zIndex: missionDialogZIndex },
          '& .MuiDialog-paper': { zIndex: missionDialogZIndex },
        }}
        PaperProps={{
          sx: { position: 'relative', zIndex: missionDialogZIndex + 1 },
        }}
      >
        <DialogTitle>Step unavailable</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{missingTargetMessage}</Typography>
        </DialogContent>
        <DialogActions>
          {state.missionMode ? (
            <>
              <Button
                onClick={() => {
                  setMissingTargetOpen(false);
                  mission?.retryCurrentTarget();
                }}
              >
                Retry
              </Button>
              {optionalStep ? (
                <Button
                  onClick={() => {
                    setMissingTargetOpen(false);
                    mission?.skipOptionalStep();
                  }}
                >
                  Skip step
                </Button>
              ) : null}
            </>
          ) : null}
          <Button
            onClick={() => {
              setMissingTargetOpen(false);
              if (state.missionMode && mission?.showFallbackAction) {
                mission.executeFallback();
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={celebrationOpen}
        autoHideDuration={4000}
        onClose={() => setCelebrationOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCelebrationOpen(false)}>
          Tour complete — nice work!
        </Alert>
      </Snackbar>
    </>
  );
};

export default TourEngineHost;
